import React, { useEffect, useState, useCallback, useRef } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Save, Trash2, PackageSearch, Download, Upload, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { downloadCsv } from "@/lib/download";

const emptyBOM = () => ({ _rid: `r_${Math.random().toString(36).slice(2, 9)}`, material: "", spec: "", quantity: 1, unit: "pcs" });

export default function PartCatalog() {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ part_code: "", description: "", bom: [emptyBOM()] });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const fileInputRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/parts").then((r) => setParts(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === parts.length) setSelected(new Set());
    else setSelected(new Set(parts.map((p) => p.id)));
  };
  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} part${selected.size > 1 ? "s" : ""}?`)) return;
    try {
      const res = await api.post("/parts/bulk-delete", { ids: Array.from(selected) });
      toast.success(`Deleted ${res.data.deleted}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error("Bulk delete failed"); }
  };
  const deleteAll = async () => {
    if (!window.confirm(`Delete ALL ${parts.length} parts? This cannot be undone.`)) return;
    try {
      const res = await api.post("/parts/bulk-delete", { all: true });
      toast.success(`Deleted ${res.data.deleted}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const onImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const form = new FormData();
    form.append("file", f);
    try {
      const res = await api.post("/parts/import", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${res.data.total} rows (${res.data.inserted} new, ${res.data.updated} updated)`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      e.target.value = "";
    }
  };

  const resetForm = () => { setForm({ part_code: "", description: "", bom: [emptyBOM()] }); setEditingId(null); setShowForm(false); };

  const submit = async () => {
    if (!form.part_code.trim()) { toast.error("Part code required"); return; }
    try {
      if (editingId) {
        await api.patch(`/parts/${editingId}`, form);
        toast.success("Part updated");
      } else {
        await api.post("/parts", form);
        toast.success("Part added");
      }
      resetForm();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const edit = (p) => {
    const bomWithIds = (p.bom?.length ? p.bom : [{}]).map((b) => ({ _rid: `r_${Math.random().toString(36).slice(2, 9)}`, material: "", spec: "", quantity: 1, unit: "pcs", ...b }));
    setForm({ part_code: p.part_code, description: p.description || "", bom: bomWithIds });
    setEditingId(p.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this part?")) return;
    await api.delete(`/parts/${id}`);
    toast.success("Deleted");
    load();
  };

  const setBOM = (i, patch) => {
    const bom = [...form.bom]; bom[i] = { ...bom[i], ...patch }; setForm({ ...form, bom });
  };
  const addBOM = () => setForm({ ...form, bom: [...form.bom, emptyBOM()] });
  const removeBOM = (i) => { const bom = [...form.bom]; bom.splice(i, 1); setForm({ ...form, bom }); };

  return (
    <Layout
      title="Part Catalog"
      subtitle="Master list of hose assemblies and their Bill of Materials"
      actions={
        <div className="flex items-center gap-2">
          <button onClick={() => downloadCsv("/export/parts", "part_catalog.csv")} data-testid="export-parts-btn" className="text-xs text-slate-700 border border-gray-200 rounded-md px-3 py-2 hover:bg-gray-50 transition-colors inline-flex items-center gap-2">
            <Download size={14} /> Export CSV
          </button>
          <button onClick={() => fileInputRef.current?.click()} data-testid="import-parts-btn" className="text-xs text-slate-700 border border-gray-200 rounded-md px-3 py-2 hover:bg-gray-50 transition-colors inline-flex items-center gap-2">
            <Upload size={14} /> Import CSV
          </button>
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onImport} />
          <Link to="/bulk-decode" data-testid="goto-bulk-decode" className="text-xs text-slate-700 border border-gray-200 rounded-md px-3 py-2 hover:bg-gray-50 transition-colors inline-flex items-center gap-2">
            <Sparkles size={14} /> Bulk decode
          </Link>
          {!showForm && (
            <button onClick={() => setShowForm(true)} data-testid="add-part-btn" className="bg-slate-900 text-white hover:bg-slate-800 rounded-md px-4 py-2 text-sm font-medium transition-colors inline-flex items-center gap-2">
              <Plus size={14} /> Add Part
            </button>
          )}
        </div>
      }
    >
      {showForm && (
        <div className="bg-white border border-gray-200 rounded-md p-6 mb-6" data-testid="part-form">
          <div className="font-display text-base font-semibold text-slate-900 mb-4">{editingId ? "Edit part" : "New part"}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase text-gray-500 font-mono tracking-widest">Part code</label>
              <input value={form.part_code} onChange={(e) => setForm({ ...form, part_code: e.target.value })} data-testid="part-code-input" className="mt-1 w-full text-sm border border-gray-200 rounded-md px-3 py-2 font-mono" />
            </div>
            <div>
              <label className="text-[11px] uppercase text-gray-500 font-mono tracking-widest">Description</label>
              <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full text-sm border border-gray-200 rounded-md px-3 py-2" />
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-widest font-mono text-gray-500">Bill of Materials</div>
              <button onClick={addBOM} className="text-xs text-slate-700 border border-gray-200 rounded-md px-2.5 py-1 hover:bg-gray-50 inline-flex items-center gap-1"><Plus size={12} /> Add</button>
            </div>
            <div className="space-y-2">
              {form.bom.map((b, i) => (
                <div key={b._rid || `bom-${i}`} className="grid grid-cols-12 gap-2 items-center">
                  <input value={b.material} onChange={(e) => setBOM(i, { material: e.target.value })} placeholder="Material" className="col-span-4 text-sm border border-gray-200 rounded-md px-2 py-1.5" />
                  <input value={b.spec} onChange={(e) => setBOM(i, { spec: e.target.value })} placeholder="Spec" className="col-span-4 text-sm border border-gray-200 rounded-md px-2 py-1.5" />
                  <input type="number" value={b.quantity} onChange={(e) => setBOM(i, { quantity: parseFloat(e.target.value) || 0 })} className="col-span-1 text-sm text-right font-mono border border-gray-200 rounded-md px-2 py-1.5" />
                  <select value={b.unit} onChange={(e) => setBOM(i, { unit: e.target.value })} className="col-span-2 text-sm border border-gray-200 rounded-md px-2 py-1.5 bg-white">
                    <option value="pcs">pcs</option>
                    <option value="meters">meters</option>
                    <option value="kg">kg</option>
                  </select>
                  <button onClick={() => removeBOM(i)} className="col-span-1 text-gray-400 hover:text-red-600"><X size={14} /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 flex items-center gap-2">
            <button onClick={submit} data-testid="submit-part-btn" className="bg-slate-900 text-white hover:bg-slate-800 rounded-md px-4 py-2 text-sm font-medium transition-colors inline-flex items-center gap-2"><Save size={14} /> {editingId ? "Update" : "Add"} part</button>
            <button onClick={resetForm} className="text-sm text-gray-600 border border-gray-200 rounded-md px-4 py-2 hover:bg-gray-50">Cancel</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        {parts.length > 0 && (
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100">
            <div className="text-xs text-gray-600" data-testid="parts-selection-count">
              {selected.size > 0 ? `${selected.size} selected` : `${parts.length} parts`}
            </div>
            <div className="flex items-center gap-2">
              {selected.size > 0 && (
                <button onClick={bulkDelete} data-testid="bulk-delete-parts-btn" className="text-xs text-red-600 border border-red-200 rounded-md px-2.5 py-1 hover:bg-red-50 inline-flex items-center gap-1">
                  <Trash2 size={12} /> Delete selected ({selected.size})
                </button>
              )}
              <button onClick={deleteAll} data-testid="delete-all-parts-btn" className="text-xs text-red-600 border border-red-200 rounded-md px-2.5 py-1 hover:bg-red-50 inline-flex items-center gap-1">
                <Trash2 size={12} /> Delete all
              </button>
            </div>
          </div>
        )}
        {loading ? (
          <div className="p-12 text-center text-sm text-gray-500">Loading...</div>
        ) : parts.length === 0 ? (
          <div className="p-16 text-center" data-testid="parts-empty">
            <PackageSearch className="mx-auto text-gray-300 mb-3" size={32} />
            <div className="font-display text-lg font-semibold text-slate-900">No parts in catalog yet</div>
            <div className="text-sm text-gray-500 mt-1">Add parts here or save them from a PO's line item.</div>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="w-10 px-4 py-3">
                  <input type="checkbox" checked={selected.size === parts.length && parts.length > 0} onChange={toggleAll} data-testid="parts-select-all" className="cursor-pointer" />
                </th>
                <th className="text-xs uppercase text-gray-500 tracking-widest font-semibold py-3 px-4 text-left">Part Code</th>
                <th className="text-xs uppercase text-gray-500 tracking-widest font-semibold py-3 px-4 text-left">Description</th>
                <th className="text-xs uppercase text-gray-500 tracking-widest font-semibold py-3 px-4 text-left">BOM</th>
                <th className="text-xs uppercase text-gray-500 tracking-widest font-semibold py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {parts.map((p) => (
                <tr key={p.id} className={`border-b border-gray-100 hover:bg-gray-50 ${selected.has(p.id) ? "bg-blue-50/40" : ""}`}>
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSelect(p.id)} data-testid={`select-part-${p.id}`} className="cursor-pointer" />
                  </td>
                  <td className="py-3 px-4 text-sm font-mono text-slate-900">{p.part_code}</td>
                  <td className="py-3 px-4 text-sm text-slate-900">{p.description || "—"}</td>
                  <td className="py-3 px-4 text-xs text-gray-600">
                    {p.decoded?.hose_spec && (
                      <div className="font-mono text-slate-700">{p.decoded.hose_spec}{p.decoded.hose_length_mm ? ` · ${p.decoded.hose_length_mm}mm` : ""}</div>
                    )}
                    {p.decoded?.fitting_a && <div>A: {p.decoded.fitting_a}</div>}
                    {p.decoded?.fitting_b && <div>B: {p.decoded.fitting_b}</div>}
                    {(p.bom || []).map((b, i) => (
                      <div key={`${p.id}-bom-${i}-${b.material}`}>{b.quantity} {b.unit} — {b.material}{b.spec ? ` (${b.spec})` : ""}</div>
                    ))}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button onClick={() => edit(p)} className="text-xs text-slate-700 border border-gray-200 rounded-md px-2.5 py-1 hover:bg-white mr-2">Edit</button>
                    <button onClick={() => remove(p.id)} data-testid={`delete-part-${p.id}`} className="text-xs text-red-600 border border-red-200 rounded-md px-2.5 py-1 hover:bg-red-50 inline-flex items-center gap-1">
                      <Trash2 size={12} /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
}
